import React from 'react';
import { Box, Typography, LinearProgress, Stack, Chip, Alert } from '@mui/material';
import { Psychology, TrendingUp, Warning, CheckCircle, Timeline } from '@mui/icons-material';
import PremiumCard from '../../ui/PremiumCard';

/**
 * WIDGET ÚNICO: Predicciones Inteligentes (Sin IA)
 * Análisis predictivo básico que Tiendanube NO tiene
 */
export default function PredictiveInsightsCard({ statsData = {}, salesData = [] }) {
  // Calcular predicciones básicas
  const predictions = React.useMemo(() => {
    const totalSales = statsData.totalSales || 0;
    const totalOrders = statsData.totalOrders || 0;
    const avgTicket = statsData.avgTicket || 0;
    const totalCustomers = statsData.totalCustomers || 0;

    if (totalSales === 0) return null;

    // Predicciones simples basadas en tendencias
    const dailyAverage = totalSales / 30; // Asumiendo 30 días
    const monthlyProjection = dailyAverage * 30;
    const quarterlyProjection = monthlyProjection * 3;
    
    // Análisis de crecimiento
    const growthRate = 0.15; // 15% crecimiento estimado
    const nextMonthProjection = monthlyProjection * (1 + growthRate);
    
    // Análisis de riesgo
    const riskFactors = [];
    if (totalCustomers < 10) riskFactors.push("Pocos clientes");
    if (avgTicket < 100000) riskFactors.push("Ticket bajo");
    if (totalOrders < 5) riskFactors.push("Pocas órdenes");
    
    const riskLevel = riskFactors.length > 2 ? "Alto" : riskFactors.length > 0 ? "Medio" : "Bajo";
    
    // Oportunidades
    const opportunities = [];
    if (avgTicket > 500000) opportunities.push("Productos premium exitosos");
    if (totalCustomers > 20) opportunities.push("Base de clientes sólida");
    if (salesData.length > 5) opportunities.push("Actividad comercial constante");

    return {
      projections: {
        nextMonth: nextMonthProjection,
        nextQuarter: quarterlyProjection,
        growthRate: growthRate * 100
      },
      risk: {
        level: riskLevel,
        factors: riskFactors,
        score: Math.max(0, 100 - (riskFactors.length * 25))
      },
      opportunities: opportunities,
      recommendations: [
        {
          priority: "Alta",
          action: "Aumentar frecuencia de compra",
          impact: "Potencial +25% en ventas",
          color: "error"
        },
        {
          priority: "Media",
          action: "Diversificar catálogo",
          impact: "Reducir dependencia de productos",
          color: "warning"
        },
        {
          priority: "Baja",
          action: "Optimizar precios",
          impact: "Mejorar márgenes",
          color: "info"
        }
      ]
    };
  }, [statsData, salesData]);

  if (!predictions) {
    return (
      <PremiumCard 
        title="Predicciones Inteligentes" 
        subtitle="Análisis predictivo exclusivo"
        icon={<Psychology />}
        color="info"
        exclusive
      >
        <Typography variant="body2" color="text.secondary" textAlign="center">
          No hay datos suficientes para predicciones
        </Typography>
      </PremiumCard>
    );
  }

  return (
    <PremiumCard 
      title="Predicciones Inteligentes"
      subtitle="Análisis predictivo que Tiendanube no ofrece"
      icon={<Psychology />}
      color="info"
      exclusive
    >
        {/* Proyecciones */}
        <Box mb={3}>
          <Typography variant="subtitle2" gutterBottom>
            📈 Proyecciones de Ventas
          </Typography>
          <Stack direction="row" spacing={2} mb={2}>
            <Box>
              <Typography variant="h6" color="primary.main">
                ${predictions.projections.nextMonth.toLocaleString()}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Próximo Mes (+{predictions.projections.growthRate.toFixed(1)}%)
              </Typography>
            </Box>
            <Box>
              <Typography variant="h6" color="secondary.main">
                ${predictions.projections.nextQuarter.toLocaleString()}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Próximo Trimestre
              </Typography>
            </Box>
          </Stack>
        </Box>

        {/* Análisis de Riesgo */}
        <Box mb={3}>
          <Typography variant="subtitle2" gutterBottom>
            ⚠️ Análisis de Riesgo
          </Typography>
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <Box flex={1}>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2">
                  Nivel de Riesgo: {predictions.risk.level}
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {predictions.risk.score}/100
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={predictions.risk.score} 
                color={predictions.risk.level === "Alto" ? "error" : predictions.risk.level === "Medio" ? "warning" : "success"}
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>
          </Box>
          {predictions.risk.factors.length > 0 && (
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {predictions.risk.factors.map((factor, i) => (
                <Chip 
                  key={i} 
                  label={factor} 
                  size="small" 
                  color="warning"
                  variant="outlined"
                />
              ))}
            </Stack>
          )}
        </Box>

        {/* Oportunidades */}
        {predictions.opportunities.length > 0 && (
          <Box mb={3}>
            <Typography variant="subtitle2" gutterBottom>
              🎯 Oportunidades Detectadas
            </Typography>
            <Stack spacing={1}>
              {predictions.opportunities.map((opp, i) => (
                <Alert key={i} severity="success" variant="outlined" sx={{ py: 0.5 }}>
                  <Typography variant="body2">{opp}</Typography>
                </Alert>
              ))}
            </Stack>
          </Box>
        )}

        {/* Recomendaciones */}
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            💡 Recomendaciones Automáticas
          </Typography>
          <Stack spacing={1}>
            {predictions.recommendations.map((rec, i) => (
              <Box key={i} display="flex" alignItems="center" gap={2}>
                <Chip 
                  label={rec.priority} 
                  size="small" 
                  color={rec.color}
                  variant="filled"
                />
                <Box flex={1}>
                  <Typography variant="body2" fontWeight={500}>
                    {rec.action}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {rec.impact}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Stack>
        </Box>

        {/* Disclaimer */}
        <Box mt={3} p={2} bgcolor="info.50" borderRadius={2}>
          <Typography variant="caption" color="info.main">
            📊 Predicciones basadas en análisis estadístico de tus datos históricos. 
            Los resultados reales pueden variar según factores del mercado.
          </Typography>
        </Box>
    </PremiumCard>
  );
}
